import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { L10nTranslationModule } from 'angular-l10n';
import { catchError, identity, of, startWith, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ResponsiveService } from '../../wonder-core-ng/services/responsive.service';
import { SessionService } from '../../wonder-core-ng/session-store/session.service';
import { DashboardApiService } from '@wonder-bi-front/dashboard-store/dashboard.api.service';
import { IDashboard } from '@wonder-bi-front/dashboard-store/interfaces/dashboard.interface';
import { rotateJiggleAnimation } from '../interfaces/animations';
import { WbiMenuLogoComponent } from './wbi-menu-logo.component';
import { Permission, PermissionsOperation } from '@wonder-bi-front/interfaces/permission.enum';
import { UserRole } from '@wonder-bi-front/user/roles.enum';
import { LayoutService } from './layout.service';
import { TenantSelectionService } from '../session/tenant-selection.service';
import { ICustomUser } from '../custom-user-store/custom-user.interface';

@Component({
  selector: 'wbi-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    RouterModule,
    L10nTranslationModule,
    MatButtonToggleModule,
    MatDividerModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    WbiMenuLogoComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [rotateJiggleAnimation],
})
export class MenuComponent implements OnInit {
  readonly activatedRoute = inject(ActivatedRoute);
  readonly sessionService = inject(SessionService);
  responsiveService = inject(ResponsiveService);
  layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly dashboardApi = inject(DashboardApiService);
  private readonly destroyRef = inject(DestroyRef);
  Roles = UserRole;

  private readonly tenantSelection = inject(TenantSelectionService);
  readonly dashboards = signal<IDashboard[]>([]);
  readonly dashboardsLoaded = signal(false);
  readonly sortedDashboards = computed(() => {
    const dashboards = [...this.dashboards()].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    return dashboards;
  });
  readonly dashboardPlaceholderOption = computed<IMenuItem | null>(() =>
    this.dashboardsLoaded() && this.dashboards().length === 0
      ? {
          name: 'dashboards',
          routerLink: ['/no-dashboards'],
          permissions: [],
          hgiIcon: 'hgi-dashboard-square-01',
        }
      : null
  );

  private readonly menuButtonHeight: number = 59.5;
  private readonly themeButtonHeight: number = 40;
  public Permission = Permission;
  public user = this.sessionService.user();
  public isClosing = signal(false);

  public readonly menuOptions = signal<IMenuItem[]>([
    {
      name: 'users',
      routerLink: ['/admin/users'],
      permissions: [Permission.USER_READ],
      hgiIcon: 'hgi-user-group',
      hideHandset: true,
    },
    {
      name: 'roles-management',
      routerLink: ['/admin/roles'],
      permissions: [Permission.ROLE_UPDATE],
      hgiIcon: 'hgi-finger-access',
      hideHandset: true,
    },
    {
      name: 'feature-flags',
      routerLink: ['/admin/feature-flags'],
      permissions: [Permission.FEATURE_FLAG_ADMIN],
      // Hugeicons has no flag; the menu also takes Material Symbols.
      matIcon: 'flag',
      hideHandset: true,
    },
    {
      name: 'queries',
      routerLink: ['/query-config'],
      permissions: [Permission.QUERY_CREATE, Permission.QUERY_UPDATE],
      permissionsOperation: 'ANY',
      hgiIcon: 'hgi-constellation',
      hideHandset: true,
    },
    {
      name: 'charts',
      routerLink: ['/chart-config'],
      permissions: [Permission.CHART_CREATE, Permission.CHART_UPDATE],
      permissionsOperation: 'ANY',
      hgiIcon: 'hgi-chart-line-data-02',
      hideHandset: true,
    },
    {
      name: 'dashboard-config',
      routerLink: ['/dashboard-config'],
      permissions: [Permission.DASHBOARD_CREATE, Permission.DASHBOARD_UPDATE],
      permissionsOperation: 'ANY',
      hgiIcon: 'hgi-dashboard-square-setting',
      hideHandset: true,
    },
    {
      name: 'conditions',
      routerLink: ['/conditions'],
      // WB-242: alerts live in this screen too, so either permission gives something to do there.
      permissions: [Permission.CONDITION_READ, Permission.EVENT_READ],
      permissionsOperation: 'ANY',
      hgiIcon: 'hgi-alert-diamond',
      hideHandset: true,
    },
    {
      name: 'action-lists',
      routerLink: ['/action-lists'],
      permissions: [Permission.EVENT_READ],
      hgiIcon: 'hgi-task-01',
      hideHandset: true,
    },
    {
      name: 'attribute-metadata',
      routerLink: ['/attribute-metadata'],
      permissions: [Permission.METADATA_READ],
      hgiIcon: 'hgi-database-setting',
      hideHandset: true,
    },
    {
      name: 'ai-metrics',
      routerLink: ['/ai-metrics'],
      permissions: [Permission.AI_METRICS_READ],
      hgiIcon: 'hgi-analytics-up',
      hideHandset: true,
    },
  ]);

  activeOption: WritableSignal<number> = signal(-1);

  private readonly _activeRouteEffect = effect(() => {
    this.allOptions();
    this.matchActiveOption(this.router.url);
  });

  // Re-align the indicator once the view is laid out (offsetTop needs the rendered rows), and
  // again whenever the viewport resizes (the row height changes at the breakpoint).
  private readonly _selectorInit = afterNextRender(() => {
    this.matchActiveOption(this.router.url);
    const onResize = (): void => this.matchActiveOption(this.router.url);
    window.addEventListener('resize', onResize, { passive: true });
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  });

  allOptions = computed(() => {
    const placeholder = this.dashboardPlaceholderOption();
    const dashboards = placeholder ? [placeholder] : this.sortedDashboards();
    return [...dashboards, ...this.menuOptions()];
  });

  @ViewChild('menuSelector') menuSelector!: ElementRef<HTMLElement>;
  @ViewChild('snav') snav!: MatSidenav;
  @Output() isMobileMenuOpened = new EventEmitter<boolean>();

  @Input() set isScrolled(value: boolean) {
    this._isScrolled.set(value);
  }
  _isScrolled = signal(false);
  isMobile = computed(() => this.responsiveService.handset());

  ngOnInit() {
    // Load once the visitor is on an app screen. A session rehydrated from localStorage is not
    // validated, so on /session/* (recovery and invitation links) an expired one would make this
    // request answer 401 and the auth interceptor would send the visitor to the login page.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        this.router.navigated ? startWith(this.router.url) : identity,
        filter((url) => !url.startsWith('/session/')),
        take(1),
        switchMap(() =>
          this.dashboardApi
            .loadDashboards(this.tenantSelection.current())
            .pipe(catchError(() => of([] as IDashboard[])))
        )
      )
      .subscribe((list) => {
        this.dashboards.set(list ?? []);
        this.dashboardsLoaded.set(true);
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.matchActiveOption(this.router.url);
    });
  }

  private matchActiveOption(url: string): void {
    const index = this.allOptions().findIndex(
      (item) =>
        url.includes((item as IMenuItem)?.routerLink?.[0]) ||
        url.includes(`/dashboard/${(item as IDashboard)?.id}`)
    );
    if (index !== -1) {
      this.setActiveOption(index);
    }
  }

  public readonly filteredMenuOptions = computed(() =>
    this.menuOptions().filter((menuItem) =>
      this.sessionService.hasPermissions(
        menuItem?.permissions as Permission[],
        (menuItem as IMenuItem)?.permissionsOperation
      )()
    )
  );

  // Was `user.company_admin`/`user.company`, relations of the retired `user.company_id` column.
  // The company is now a column of the tenant's own `user_extras`, served as `extras`.
  public companyId = computed(() => (this.user as ICustomUser | undefined)?.extras?.company_id);

  toggleSnav(): void {
    this.snav.toggle();
    this.isMobileMenuOpened.emit(this.snav.opened);
  }

  setActiveOption(index: number): void {
    this.activeOption.set(index);

    const selector = this.menuSelector?.nativeElement;
    if (selector) {
      // Use the actual option element's offset so the indicator stays aligned regardless of
      // row height, dividers or viewport size (no reliance on a fixed row-height constant).
      const options = selector.parentElement?.querySelectorAll<HTMLElement>('a.toggle-option');
      const el = options?.[index];
      const translation = el ? el.offsetTop : index * this.menuButtonHeight;
      selector.style.transform = 'translateY(' + translation + 'px)';
    }

    const currentRoute = this.router.url;
    const menuItem = this.allOptions()[index];
    if (!menuItem) return;
    const routerLink = (menuItem as IMenuItem).routerLink;
    const dashboardId = (menuItem as IDashboard).id;
    if (routerLink?.length && !currentRoute.includes(routerLink[0])) {
      this.router.navigate(routerLink);
    } else if (dashboardId && !currentRoute.includes(`/dashboard/${dashboardId}`)) {
      this.router.navigate(['/dashboard', dashboardId]);
    }
  }

  logout() {
    this.router.navigate(['session/logout']);
  }

  closeMenu() {
    this.isClosing.set(true);
    this.snav.close();
    this.isMobileMenuOpened.emit(this.snav.opened);
    setTimeout(() => {
      this.isClosing.set(false);
    }, 650); // Match this with the animation duration (get from css var?)
  }
}
export interface IMenuItem {
  name: string;
  shortName?: string;
  routerLink: string[];
  permissions?: Permission[];
  /**
   * How `permissions` is read. Defaults to 'ALL' (every permission required), which is what an
   * entry gated by a single permission wants. An authoring entry lists create and update and asks
   * for 'ANY': holding either is enough to have something to do there.
   */
  permissionsOperation?: PermissionsOperation;
  matIcon?: string;
  svgIcon?: string;
  hgiIcon?: string;
  hideHandset?: boolean;
}
